/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import { Study, Object, Experiment } from "./types";
import { SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS } from "../../../containers/pages/SensingSystemArchitectureDesign/constants";
import { config } from "../../../AppConfig/config";
import ADAS_PROGRAMS_CONSTANTS from "../../../containers/pages/AdasPrograms/constants";
import { Requests } from "../../../containers/pages/SensingSystemArchitectureDesign/types";
import toast from "react-hot-toast";


/** Fetches all Sensing System Architecture Design studies */
export const fetchStudies = createAsyncThunk('sensingSystemArchitectureDesign/fetchStudies', async (idToken: string) => {
  const fetchStudiesUrl = `${config.url.API_URL}${SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.endpoints.fetchStudies}`
  const response = await fetch(fetchStudiesUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
  })
  if (!response.ok) {
    throw new Error('Failed to fetch studies')
  }
  return (await response.json()) as Study[]
})

/** Fetches all experiments for a Sensing System Architecture Design study */
export const fetchExperiments = createAsyncThunk('sensingSystemArchitectureDesign/fetchExperiments', async ({ idToken, studyId }: { idToken: string, studyId: string }) => {
  const fetchExperimentsUrl = `${config.url.API_URL}${SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.fetchExperiments}`
  const response = await fetch(fetchExperimentsUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({studyId})
  })
  if (!response.ok) {
    throw new Error('Failed to fetch experiments')
  }
  return (await response.json()) as Experiment[]
})

/** Fetches the object data for an ADAS Program */
export const fetchObjectsForAdasProgram = createAsyncThunk('sensingSystemArchitectureDesign/fetchObjectsForAdasProgram', async ({ idToken, adasProgramId }: { idToken: string, adasProgramId: string }) => {
  const fetchObjectsForAdasProgramUrl = `${config.url.API_URL}${ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorBom.fetchProgramPropertiesEndpoint}`
  const response = await fetch(fetchObjectsForAdasProgramUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({adasProgramId})
  })
  if (!response.ok) {
    throw new Error('Failed to fetch the objects for the ADAS Program')
  }
  return (await response.json()).programPropertiesTable.objectsData as Object[]
})

export interface IFetchMergedDataSetsForAdasProgramPayload {
  idToken: string
  adasProgramId: string
  greenLineInfo?: {
    studyId: string
  }
}
/** Fetches the merged data sets data for an ADAS Program */
export const fetchMergedDataSetsForAdasProgram = createAsyncThunk('sensingSystemArchitectureDesign/fetchMergedDataSetsForAdasProgram',
  async (payload: IFetchMergedDataSetsForAdasProgramPayload) => {
    const { idToken, adasProgramId, greenLineInfo } = payload
    const fetchMergedDataSetsForAdasProgramUrl = `${config.url.API_URL}${ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchMergedDataSetsTree}`
    const requestBody = {
      adasProgramId,
      greenLineInfo
    }
    const response = await fetch(fetchMergedDataSetsForAdasProgramUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(requestBody)
    })
    if (!response.ok) {
      throw new Error('Failed to fetch the data sets for the ADAS Program')
    }
    return (await response.json()) as any
  })

/** Generates the green lines for an experiment */
export const generateGreenLinesForExperiment = createAsyncThunk('sensingSystemArchitectureDesign/generateGreenLinesForExperiment', async ({ idToken, adasProgramId, experiment }: { idToken: string, adasProgramId: string, experiment: Experiment }) => {
  const generateGreenLinesUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.sensingSystemArchitectureDesignOrchestrator
  const response = await fetch(generateGreenLinesUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({action: Requests.GENERATE_GREEN_LINES, adasProgramId, experiment})
  })
  if (!response.ok) {
    toast.error('Failed to generate green lines for the experiment')
    throw new Error('Failed to generate green lines for the experiment')
  }
  return (await response.json()) as any
})

/** Commonizes the selected green lines for an experiment */
export const commonizeGreenLinesForExperiment = createAsyncThunk('sensingSystemArchitectureDesign/commonizeGreenLinesForExperiment', async ({ idToken, adasProgramId, experiment }: { idToken: string, adasProgramId: string, experiment: Experiment }) => {
  const commonizeGreenLinesUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.sensingSystemArchitectureDesignOrchestrator
  const response = await fetch(commonizeGreenLinesUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({action: Requests.COMMONIZE_GREEN_LINES, adasProgramId, experiment})
  })
  if (!response.ok) {
    toast.error('Failed to commonize green lines for the experiment')
    throw new Error('Failed to commonize green lines for the experiment')
  }
  return (await response.json()) as any
})

/** Smooths the selected green lines for an experiment */
export const smoothGreenLinesForExperiment = createAsyncThunk('sensingSystemArchitectureDesign/smoothGreenLinesForExperiment', async ({ idToken, adasProgramId, experiment, smoothnessFactor }: { idToken: string, adasProgramId: string, experiment: Experiment, smoothnessFactor: number }) => {
  const smoothGreenLinesUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.sensingSystemArchitectureDesignOrchestrator
  const response = await fetch(smoothGreenLinesUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({action: Requests.SMOOTH_GREEN_LINES, adasProgramId, experiment, smoothnessFactor})
  })
  if (!response.ok) {
    toast.error('Failed to smooth green lines for the experiment')
    throw new Error('Failed to smooth green lines for the experiment')
  }
  return (await response.json()) as any
})

/** Symmetrizes the selected green lines for an experiment */
export const symmetrizeGreenLinesForExperiment = createAsyncThunk('sensingSystemArchitectureDesign/symmetrizeGreenLinesForExperiment', async ({ idToken, adasProgramId, experiment }: { idToken: string, adasProgramId: string, experiment: Experiment }) => {
  const symmetrizeGreenLinesUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.sensingSystemArchitectureDesignOrchestrator
  const response = await fetch(symmetrizeGreenLinesUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({action: Requests.SYMMETRIZE_GREEN_LINES, adasProgramId, experiment})
  })
  if (!response.ok) {
    toast.error('Failed to symmetrize green lines for the experiment')
    throw new Error('Failed to symmetrize green lines for the experiment')
  }
  return (await response.json()) as any
})

/** Generates the blue lines for an experiment */
export const generateBlueLinesForExperiment = createAsyncThunk('sensingSystemArchitectureDesign/generateBlueLinesForExperiment', async ({ idToken, adasProgramId, experiment, sensorIdsToGenerate }: { idToken: string, adasProgramId: string, experiment: Experiment, sensorIdsToGenerate: Record<string, true> }) => {
  const generateBlueLinesUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.sensingSystemArchitectureDesignOrchestrator
  const response = await fetch(generateBlueLinesUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({action: Requests.GENERATE_BLUE_LINES, adasProgramId, experiment, sensorIdsToGenerate})
  })
  if (!response.ok) {
    toast.error('Failed to generate blue lines for the experiment')
    throw new Error('Failed to generate blue lines for the experiment')
  }
  return (await response.json()) as any
})

/** Generates the violations for an experiment */
export const generateViolationsForExperiment = createAsyncThunk('sensingSystemArchitectureDesign/generateBlueLinesForExperiment', async ({ idToken, adasProgramId, experiment, sensorIdsToGenerate }: { idToken: string, adasProgramId: string, experiment: Experiment, sensorIdsToGenerate: Record<string, true> }) => {
  const generateViolationsUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.sensingSystemArchitectureDesignOrchestrator
  const response = await fetch(generateViolationsUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({action: Requests.GENERATE_VIOLATIONS, adasProgramId, experiment, sensorIdsToGenerate})
  })
  if (!response.ok) {
    toast.error('Failed to generate violations for the experiment')
    throw new Error('Failed to generate violations for the experiment')
  }
  return (await response.json()) as any
})